import { Card, Flex, Grid, Heading, Paragraph, Text, Tooltip, TooltipPopper } from '@zillow/constellation';
import type { BackgroundReport } from '@zg-rentals/ts-capps-client';
import { getReportIdsText, getReportStatusDescription } from '@zg-rentals/applications-utils';
import { UserType } from '../../models/report-models';

const conditionalApprovalTooltipText =
  'This information is unavailable because your area requires you to conditionally approve this application before you can see the criminal record summary of the background report. You can take action below in the criminal record section.';

export function BackgroundReportSummary({
  userType,
  backgroundReport,
  zipCode,
  requestedDateTime,
  expirationDateTime,
  requiresConditionalApproval,
}: {
  userType: UserType;
  backgroundReport: BackgroundReport;
  zipCode: string;
  requestedDateTime: string;
  expirationDateTime: string;
  requiresConditionalApproval: boolean;
}) {
  const evictionRecordsCount = backgroundReport?.evictionReport?.records?.length || 0;
  const criminalRecordsCount = backgroundReport?.criminalReport?.records?.length || 0;
  const criminalRecordUnavailableDescription =
    requiresConditionalApproval && userType === UserType.Landlord
      ? 'Unavailable'
      : getReportStatusDescription(backgroundReport?.criminalReport?.reportStatus);
  const housingCourtRecordUnavailableDescription = getReportStatusDescription(
    backgroundReport?.evictionReport?.reportStatus,
  );
  const reportIds = getReportIdsText([
    backgroundReport?.criminalReport?.responseId,
    backgroundReport?.evictionReport?.responseId,
  ]);

  return (
    <>
      <Heading level={5} as="h2" marginY="md">
        Background check summary
      </Heading>
      <Grid display="grid" gridTemplateColumns="repeat(2, 1fr)" gap="sm" marginBottom="sm">
        <Grid gridColumnEnd={{ default: 'span 2', md_gte: 'span 1' }}>
          <Card cardType="secondary" cardElevation={0}>
            <Flex display="flex" justifyContent="center" alignItems="center" flexDirection="column">
              <Paragraph>Housing court records</Paragraph>
              <Text fontType="h3" marginY="sm">
                {housingCourtRecordUnavailableDescription ?? evictionRecordsCount}
              </Text>
            </Flex>
          </Card>
        </Grid>
        <Grid gridColumnEnd={{ default: 'span 2', md_gte: 'span 1' }}>
          <Card cardType="secondary" cardElevation={0}>
            <Flex display="flex" justifyContent="center" alignItems="center" flexDirection="column">
              <Flex display="flex" alignItems="center">
                <Paragraph marginRight="xs">Criminal records</Paragraph>
                {requiresConditionalApproval && userType === UserType.Landlord && (
                  <TooltipPopper
                    triggered={<Tooltip body={<Paragraph>{conditionalApprovalTooltipText}</Paragraph>} />}
                  />
                )}
              </Flex>
              <Text fontType="h3" marginY="sm">
                {criminalRecordUnavailableDescription ?? criminalRecordsCount}
              </Text>
            </Flex>
          </Card>
        </Grid>
      </Grid>
      <Heading level={5} as="h2" marginY="md">
        Report detail
      </Heading>
      <Paragraph>Report ID: {reportIds}</Paragraph>
      <Paragraph>Rental property ZIP Code: {zipCode}</Paragraph>
      <Paragraph>Created: {requestedDateTime}</Paragraph>
      <Paragraph>Expiry: {expirationDateTime}</Paragraph>
    </>
  );
}
